import { navigationDrawerStyles as styles } from "@insite/content-library/Components/NavigationDrawer";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { colorStrings, fontFamilyHeading, manualStyles, mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import { css } from "styled-components";

const reusableTypography = (typo?: TypographyPresentationProps): TypographyPresentationProps => ({
    ...typo,
    color: colorStrings.textMain,
    size: "16px",
    lineHeight: "22px",
    weight: 700,
    transform: "none",
    fontFamily: fontFamilyHeading,
});

styles.menuTriggerButton = {
    ...styles.menuTriggerButton,
    mergeCss: true,
    color: "primary",
    css: css`
        ${styles.menuTriggerButton?.css}
        ${mediaQueriesBP.maxLG} {
            padding: 0;

            &:hover,
            &:focus,
            &:active {
                background: ${getColor(colorStrings.primaryMain)};
                border: none !important;
                outline: none !important;
            }
        }

        ${mediaQueriesBP.maxMD} {
            padding: 0 16px;
        }
    `,
};

styles.drawer = {
    ...styles.drawer,
    cssOverrides: {
        ...styles.drawer?.cssOverrides,
        drawerBody: css`
            ${styles.drawer?.cssOverrides?.drawerBody}
            background: ${getColor(colorStrings.commonGlobalWhite)};
        `,
    },
};

styles.drawerSectionWrapper = {
    ...styles.drawerSectionWrapper,
    css: css`
        ${styles.drawerSectionWrapper?.css}

        &:first-child {
            background: ${getColor(colorStrings.primaryMain)};
            padding-bottom: 0;

            & > .panel-menu,
            & > .main-navigation-row {
                border-bottom: 1px solid ${getColor(colorStrings.commonGlobalWhite)};
                width: 100%;
            }

            & > .main-navigation-row {
                padding: 8px 16px;
            }

            & > *:last-child {
                border: 0;
            }
        }

        margin-top: 0;
    `,
};

styles.panelMenu = {
    ...styles.panelMenu,
    headerColor: colorStrings.primaryMain,
    bodyColor: colorStrings.commonGlobalWhite,
    panelRowProps: {
        ...styles.panelMenu?.panelRowProps,
        css: css`
            ${styles.panelMenu?.panelRowProps?.css}
            margin: 0;
        `,
        isCurrentCss: css`
            ${manualStyles.headerMobileSelected}
        `,
    },
    childTypographyProps: reusableTypography(styles.panelMenu?.childTypographyProps),
    cssOverrides: {
        children: css`
            ${styles.panelMenu?.cssOverrides?.children};

            ${StyledButton} {
                padding: 8px 16px;
                background: transparent;
                border-bottom: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
                width: 100%;
                span {
                    color: ${getColor(colorStrings.primaryMain)};
                }
            }

            > button {
                width: calc(100% - 2px);
            }
        `,
    },
};

styles.menuRowIcon = {
    ...styles.menuRowIcon,
    color: colorStrings.primaryMain,
};

styles.userRowTypography = {
    ...reusableTypography(styles.userRowTypography),
    color: colorStrings.commonGlobalWhite,
};

styles.punchOutUserRowText = reusableTypography(styles.punchOutUserRowText);

styles.mainNavigationRow = {
    ...styles.mainNavigationRow,
    color: colorStrings.primaryMain,
    css: css`
        ${styles.mainNavigationRow?.css}
        padding: 8px 16px;
    `,
};

styles.mainNavigationRowIcon = {
    ...styles.mainNavigationRowIcon,
    color: colorStrings.commonGlobalWhite,
};

styles.mainNavigationRowTypography = {
    ...reusableTypography(styles.mainNavigationRowTypography),
    color: colorStrings.commonGlobalWhite,
};

styles.logoLinks = {
    ...styles.logoLinks,
    typographyProps: reusableTypography(styles.logoLinks?.typographyProps),
    css: css`
        ${styles.logoLinks?.css}
        background: ${getColor(colorStrings.commonGlobalWhite)};
        border-bottom: 1px solid ${getColor(colorStrings.commonBorder)};
        padding: 8px 16px;
        margin: 0;
        display: none;
        span {
            color: ${getColor(colorStrings.primaryMain)};
        }

        ${IconWrapper} {
            color: ${getColor(colorStrings.primaryMain)};
        }
    `,
    color: colorStrings.commonPrimaryAlt,
    isCurrentCss: css`
        ${manualStyles.headerMobileSelected}
    `,
};

styles.currencySymbol = {
    ...styles.currencySymbol,
    color: colorStrings.primaryMain,
};

styles.changeCustomerRow = {
    ...styles.changeCustomerRow,
    css: css`
        ${styles.changeCustomerRow?.css}
        background: ${getColor(colorStrings.commonGlobalWhite)};
    `,
};

styles.signOutRow = {
    ...styles.signOutRow,
    css: css`
        ${styles.signOutRow?.css}
        background: ${getColor(colorStrings.commonGlobalWhite)};
        border-bottom: 1px solid ${getColor(colorStrings.commonBorder)};
        padding: 8px 16px;
        width: 100%;

        span {
            color: ${getColor(colorStrings.primaryMain)};
        }
    `,
    color: colorStrings.commonPrimaryAlt,
};

styles.signOutRowText = reusableTypography(styles.signOutRowText);

styles.changeCustomerRowContainer = {
    ...styles.changeCustomerRowContainer,
    css: css`
        ${styles.changeCustomerRowContainer?.css}

        font-weight: 700;
        font-family: ${fontFamilyHeading};
    `,
};
